/**
 * Modified LogRocket Fuzzy Search Sanitizer
 *
 * This plugin extends the original functionality to also sanitize query parameters in URLs.
 */

interface LogRocketNetworkRequest {
  body: any;
  headers: Record<string, string>;
  url: string;
  method: string;
}

interface LogRocketNetworkResponse {
  body: any;
  headers: Record<string, string>;
  status: number;
}

// Main export class
export default class LogrocketFuzzySanitizer {
  /**
   * Set up the sanitizer with a list of private field names to scrub
   * @param privateFieldNames Array of strings that represent the private field names
   * @param options Additional options for the sanitizer
   * @returns Object containing requestSanitizer and responseSanitizer functions
   */
  static setup(
    privateFieldNames: string[],
    options: {
      urlSanitizerEnabled?: boolean; // New option to enable/disable URL sanitizing
    } = {}
  ) {
    // Default options
    const defaultOptions = {
      urlSanitizerEnabled: true,
    };

    // Merge options with defaults
    const mergedOptions = { ...defaultOptions, ...options };

    return {
      requestSanitizer: (request: LogRocketNetworkRequest) => {
        if (!request) {
          return null;
        }

        // Clone the request to avoid modifying the original
        const sanitizedRequest = { ...request };

        // Sanitize the body if it exists
        if (sanitizedRequest.body) {
          sanitizedRequest.body = LogrocketFuzzySanitizer.sanitizeData(
            sanitizedRequest.body,
            privateFieldNames
          );
        }

        // Sanitize URL query parameters if enabled
        if (mergedOptions.urlSanitizerEnabled && sanitizedRequest.url) {
          sanitizedRequest.url = LogrocketFuzzySanitizer.sanitizeUrl(
            sanitizedRequest.url,
            privateFieldNames
          );
        }

        return sanitizedRequest;
      },
      responseSanitizer: (response: LogRocketNetworkResponse) => {
        if (!response) {
          return null;
        }

        // Clone the response to avoid modifying the original
        const sanitizedResponse = { ...response };

        // Sanitize the body if it exists
        if (sanitizedResponse.body) {
          sanitizedResponse.body = LogrocketFuzzySanitizer.sanitizeData(
            sanitizedResponse.body,
            privateFieldNames
          );
        }

        return sanitizedResponse;
      },
    };
  }

  /**
   * Sanitize data by recursively traversing objects and arrays
   * looking for field names that match the private field names
   */
  private static sanitizeData(data: any, privateFieldNames: string[]): any {
    // Handle null or undefined data
    if (data === null || data === undefined) {
      return data;
    }

    // If JSON string, convert the data to json.
    if (this.isJsonString(data)) {
      data = JSON.parse(data);
    }

    // Handle primitive data types
    if (typeof data !== 'object') {
      return data;
    }

    // Handle arrays
    if (Array.isArray(data)) {
      return data.map((item) => this.sanitizeData(item, privateFieldNames));
    }

    // Handle objects
    const sanitizedData = { ...data };
    for (const key in sanitizedData) {
      // Check if the key exactly matches any private field names
      const keyLower = key.toLowerCase();
      const shouldSanitize = privateFieldNames.some(
        (field) => keyLower === field.toLowerCase()
      );

      if (shouldSanitize) {
        // Sanitize the field by setting it to '[REDACTED]'
        sanitizedData[key] = 'REDACTED';
      } else if (
        typeof sanitizedData[key] === 'object' &&
        sanitizedData[key] !== null
      ) {
        // Recursively sanitize nested objects
        sanitizedData[key] = this.sanitizeData(
          sanitizedData[key],
          privateFieldNames
        );
      }
    }

    return sanitizedData;
  }

  /**
   * NEW FUNCTION: Sanitize URL query parameters
   * This function checks if the URL has query parameters and sanitizes them if they match private field names
   */
  private static sanitizeUrl(url: string, privateFieldNames: string[]): string {
    // Skip sanitization for empty or invalid URLs
    if (!url || typeof url !== 'string') {
      return url;
    }

    // Check if the URL has query parameters
    if (!url.includes('?')) {
      return url;
    }

    try {
      // Try to parse the URL
      let parsedUrl: URL;

      // Handle relative URLs by trying to create a URL with a base
      if (url.startsWith('/') || !url.includes('://')) {
        try {
          // Use current origin as base for relative URLs
          parsedUrl = new URL(url, window.location.origin);
        } catch {
          // If window.location is not available (e.g., in Node.js)
          // or if URL is still invalid, try a dummy base
          parsedUrl = new URL(url, 'http://surveysdev.lexiconservices.com');
        }
      } else {
        // For absolute URLs
        parsedUrl = new URL(url);
      }

      const searchParams = parsedUrl.searchParams;
      let paramSanitized = false;

      // Check each query parameter against the private field names
      for (const paramName of searchParams.keys()) {
        const paramNameLower = paramName.toLowerCase();
        const shouldSanitize = privateFieldNames.some(
          (field) => paramNameLower === field.toLowerCase()
        );

        if (shouldSanitize) {
          // Replace the parameter value with '[REDACTED]'
          searchParams.set(paramName, 'REDACTED');
          paramSanitized = true;
        }
      }

      // Only convert back to string if sanitization occurred
      if (paramSanitized) {
        // For relative URLs, make sure we don't return with the dummy origin
        if (url.startsWith('/')) {
          return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
        }
        return parsedUrl.toString();
      }

      // Return original URL if no sanitization needed
      return url;
    } catch (error) {
      // If URL parsing fails, return the original URL
      console.warn('Failed to sanitize URL:', error);
      return url;
    }
  }

  private static isJsonString(str: string) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }
}

// Example usage:
/*
import LogRocket from 'logrocket';
import LogrocketFuzzySanitizer from 'logrocket-fuzzy-search-sanitizer';

const privateFieldNames = [
  'ssn',
  'firstName',
  'token',
  'password',
  'secret',
  'apiKey'
];

const { requestSanitizer, responseSanitizer } = LogrocketFuzzySanitizer.setup(privateFieldNames);

LogRocket.init('app/id', {
  network: {
    requestSanitizer,
    responseSanitizer
  }
});
*/
